import { effect, inject, Injectable } from '@angular/core';

import { validateLiveTileSnapshot } from '../../live-tiles/live-tile-snapshot.validator';
import type { UtcDay } from '../../live-tiles/live-tile.model';
import { LiveTileStore } from '../../live-tiles/live-tile.store';
import { APP_CONFIG } from '../config/app-config.model';
import { CLIENT_ROLES, type Session } from '../session/session.model';
import { SessionService } from '../session/session.service';
import {
  RealtimeConnection,
  RealtimeConnectionFactory,
  type TileUpdateHandler,
} from './realtime-connection.factory';
import { RealtimeGrantClient, type RealtimeGrant } from './realtime-grant.client';
import { RealtimeRetryPolicy } from './realtime-retry.policy';
import type { RealtimeConnectionState, RealtimeLifecycleResult } from './realtime-state.model';
import { RealtimeStatusService } from './realtime-status.service';
import {
  RealtimeTelemetry,
  type RealtimeAttemptBucket,
  type RealtimeDelayBucket,
  type RealtimeDurationBucket,
} from './realtime-telemetry';

export interface RealtimeTransportRestoration {
  readonly id: number;
  readonly day: UtcDay;
}

export type RealtimeTransportRestoredHandler = (
  restoration: RealtimeTransportRestoration,
) => void;

@Injectable()
export class RealtimeSubscriberService {
  private readonly config = inject(APP_CONFIG);
  private readonly sessionService = inject(SessionService);
  private readonly grantClient = inject(RealtimeGrantClient);
  private readonly connectionFactory = inject(RealtimeConnectionFactory);
  private readonly retryPolicy = inject(RealtimeRetryPolicy);
  private readonly store = inject(LiveTileStore);
  private readonly status = inject(RealtimeStatusService);
  private readonly telemetry = inject(RealtimeTelemetry);

  readonly connectionState = this.status.state;

  private generation = 0;
  private demanded = false;
  private activeDay: UtcDay | null = null;
  private activeClientCode: string | null = null;
  private abortController: AbortController | null = null;
  private connection: RealtimeConnection | null = null;
  private tileHandler: TileUpdateHandler | null = null;
  private hasConnected = false;
  private restorationId = 0;
  private pendingRestorationId: number | null = null;
  private transportRestoredHandler: RealtimeTransportRestoredHandler | null = null;
  private connectionAttemptCount = 0;
  private reconnectAttemptCount = 0;
  private clientSessionChangedHandler: (() => void) | null = null;

  constructor() {
    effect(() => this.reconcileSession(this.sessionService.session()));
  }

  activate(day: UtcDay): void {
    if (this.demanded) return;
    this.demanded = true;
    this.activeDay = day;
    this.reconcileSession(this.sessionService.session());
  }

  deactivate(): void {
    if (!this.demanded) return;
    this.demanded = false;
    this.activeDay = null;
    this.activeClientCode = null;
    this.teardown('disabled');
  }

  updateActiveDay(day: UtcDay): void {
    if (!this.demanded) return;
    this.activeDay = day;
  }

  onTransportRestored(handler: RealtimeTransportRestoredHandler): () => void {
    this.transportRestoredHandler = handler;
    return () => {
      if (this.transportRestoredHandler === handler) this.transportRestoredHandler = null;
    };
  }

  /**
   * Tenant-switch notification (FR-005): fired after a real client change (a
   * prior client was active) once the store has been cleared and the transport
   * restarted, so the consumer can re-establish the tenant's authoritative
   * baseline. Not fired for the initial null → client activation.
   */
  onClientSessionChanged(handler: () => void): () => void {
    this.clientSessionChangedHandler = handler;
    return () => {
      if (this.clientSessionChangedHandler === handler) this.clientSessionChangedHandler = null;
    };
  }

  completeTransportRestoration(restorationId: number): void {
    if (
      this.pendingRestorationId !== restorationId
      || this.connection === null
      || !this.demanded
    ) {
      return;
    }

    this.pendingRestorationId = null;
    this.transitionTo('connected');
  }

  holdTransportUntilCurrent(day: UtcDay): RealtimeTransportRestoration | null {
    if (!this.demanded || this.connection === null || this.activeDay !== day) return null;
    return this.beginTransportRestoration(day);
  }

  private reconcileSession(session: Session): void {
    if (
      !this.demanded
      || this.activeDay === null
      || !this.config.signalREnabled
      || !isEligibleClientSession(session)
    ) {
      // Eligibility loss (sign-out, role change, or a client change observed
      // while inactive): the previous tenant's tile values must never outlive
      // the session they belong to, even with no active transport.
      this.store.beginTenantSession(null);
      if (this.activeClientCode !== null || this.hasActiveAttempt()) {
        this.activeClientCode = null;
        this.teardown('disabled');
      } else {
        this.transitionTo('disabled');
      }
      return;
    }

    if (this.activeClientCode === session.clientCode) return;
    // Tenant switch (FR-005): the previous tenant's tile values must never be
    // displayed under the new identity. On a REAL switch (a prior client was
    // active) clear the store, restart the transport, and notify the consumer
    // so it re-establishes the baseline. The initial activation (null → client)
    // is not a switch: the consumer's activation refresh already establishes
    // the baseline, and a redundant one would double the refresh traffic.
    const isSwitch = this.activeClientCode !== null || this.hasActiveAttempt();
    if (isSwitch) this.store.beginTenantSession(session.clientCode);
    if (isSwitch) this.teardown('disabled');
    this.activeClientCode = session.clientCode;
    this.startConnection(session.clientCode);
    if (isSwitch) this.clientSessionChangedHandler?.();
  }

  private startConnection(clientCode: string): void {
    const generation = ++this.generation;
    const abortController = new AbortController();
    this.abortController = abortController;
    this.hasConnected = false;
    this.pendingRestorationId = null;
    this.connectionAttemptCount = 0;
    this.reconnectAttemptCount = 0;
    this.retryPolicy.resetDelayProgression();
    this.transitionTo('connecting');
    if (!this.retryPolicy.admitAttempt()) {
      this.transitionTo('fallback');
      void this.scheduleRetry(clientCode, generation, abortController.signal, false);
      return;
    }
    void this.connect(clientCode, generation, abortController.signal, false);
  }

  private teardown(nextState: RealtimeConnectionState): void {
    this.generation += 1;
    this.abortController?.abort();
    this.abortController = null;

    const connection = this.connection;
    const tileHandler = this.tileHandler;
    this.connection = null;
    this.tileHandler = null;
    this.hasConnected = false;
    this.pendingRestorationId = null;
    this.transitionTo(nextState);
    if (connection === null) return;
    if (tileHandler !== null) connection.offTileUpdate(tileHandler);
    void connection.stop().catch(() => undefined);
  }

  private async connect(
    clientCode: string,
    generation: number,
    signal: AbortSignal,
    reconnecting: boolean,
  ): Promise<void> {
    const attemptStartedAt = performance.now();
    if (!reconnecting) {
      this.connectionAttemptCount += 1;
      this.telemetry.connectionAttempt(attemptBucket(this.connectionAttemptCount));
    }
    const negotiationStartedAt = performance.now();
    let grantResult: RealtimeLifecycleResult<RealtimeGrant> | null;
    try {
      grantResult = await this.grantClient.negotiate(clientCode, signal);
    } catch {
      if (!this.isCurrent(generation)) return;
      this.telemetry.negotiationOutcome(
        'network',
        durationBucket(performance.now() - negotiationStartedAt),
      );
      this.recordAttemptOutcome(reconnecting, 'failed', attemptStartedAt);
      this.transitionTo(reconnecting ? 'reconnecting' : 'fallback');
      void this.scheduleRetry(clientCode, generation, signal, reconnecting);
      return;
    }
    if (!this.isCurrent(generation)) return;
    if (!grantResult.ok) {
      this.telemetry.negotiationOutcome(
        grantResult.reason,
        durationBucket(performance.now() - negotiationStartedAt),
      );
      this.recordAttemptOutcome(reconnecting, 'failed', attemptStartedAt);
      this.transitionTo(
        reconnecting && grantResult.disposition === 'transient' ? 'reconnecting' : 'fallback',
      );
      if (grantResult.disposition === 'transient') {
        void this.scheduleRetry(clientCode, generation, signal, reconnecting);
      }
      return;
    }
    this.telemetry.negotiationOutcome(
      'succeeded',
      durationBucket(performance.now() - negotiationStartedAt),
    );

    const currentSession = this.sessionService.session();
    if (
      !isEligibleClientSession(currentSession)
      || currentSession.clientCode !== clientCode
      || grantResult.value.clientCode !== currentSession.clientCode
    ) {
      this.recordAttemptOutcome(reconnecting, 'failed', attemptStartedAt);
      this.transitionTo('fallback');
      return;
    }

    const connection = this.connectionFactory.create(grantResult.value);
    grantResult = null;
    const tileHandler: TileUpdateHandler = (message) => {
      if (!this.isCurrent(generation)) return;
      const session = this.sessionService.session();
      if (
        !isEligibleClientSession(session)
        || session.clientCode !== clientCode
        || this.activeClientCode !== clientCode
        || this.activeDay === null
      ) {
        return;
      }
      const validation = validateLiveTileSnapshot(message);
      if (!validation.ok) {
        this.telemetry.messageRejected(validation.reason);
        return;
      }
      if (validation.value.clientCode !== session.clientCode) {
        this.telemetry.messageRejected('client-mismatch');
        return;
      }
      if (validation.value.day !== this.activeDay) {
        this.telemetry.messageRejected('day-mismatch');
        return;
      }
      if (this.status.state() !== 'connected') {
        this.telemetry.messageRejected('no-authoritative-baseline');
        return;
      }
      const outcome = this.store.applyLive(validation.value);
      if (outcome !== 'applied') this.telemetry.messageRejected(outcome);
    };

    connection.onTileUpdate(tileHandler);
    connection.onClosed(() => {
      if (!this.isCurrent(generation) || this.connection !== connection) return;
      this.releaseConnection(connection, tileHandler);
      this.pendingRestorationId = null;
      this.transitionTo('reconnecting');
      void this.scheduleRetry(clientCode, generation, signal, true);
    });
    this.connection = connection;
    this.tileHandler = tileHandler;

    try {
      await connection.start();
    } catch {
      if (this.isCurrent(generation)) {
        this.recordAttemptOutcome(reconnecting, 'failed', attemptStartedAt);
        this.releaseConnection(connection, tileHandler);
        this.transitionTo(reconnecting ? 'reconnecting' : 'fallback');
        void this.scheduleRetry(clientCode, generation, signal, reconnecting);
      }
      return;
    }
    if (this.isCurrent(generation) && this.connection === connection) {
      this.recordAttemptOutcome(reconnecting, 'succeeded', attemptStartedAt);
      this.retryPolicy.resetDelayProgression();
      // A successful connection ends the outage: the next disconnect starts a
      // fresh attempt sequence so telemetry attempt/delay buckets stay honest.
      this.reconnectAttemptCount = 0;
      const isRestoration = reconnecting || this.hasConnected;
      this.hasConnected = true;
      if (isRestoration && this.transportRestoredHandler !== null && this.activeDay !== null) {
        const restoration = this.beginTransportRestoration(this.activeDay);
        this.transportRestoredHandler(restoration);
      } else {
        this.pendingRestorationId = null;
        this.transitionTo('connected');
      }
    }
  }

  private beginTransportRestoration(day: UtcDay): RealtimeTransportRestoration {
    const restoration = { id: ++this.restorationId, day };
    this.pendingRestorationId = restoration.id;
    this.transitionTo('fallback');
    return restoration;
  }

  private async scheduleRetry(
    clientCode: string,
    generation: number,
    signal: AbortSignal,
    reconnecting: boolean,
  ): Promise<void> {
    while (this.isCurrent(generation)) {
      this.reconnectAttemptCount += 1;
      const attempt = attemptBucket(this.reconnectAttemptCount);
      const delay = retryDelayBucket(this.reconnectAttemptCount);
      this.telemetry.reconnectAttempt(attempt, delay);
      let admitted: boolean;
      try {
        admitted = await this.retryPolicy.waitForRetry(signal);
      } catch {
        this.telemetry.reconnectOutcome('cancelled', durationBucket(0));
        return;
      }
      if (!this.isCurrent(generation)) return;
      if (!admitted) {
        this.telemetry.reconnectOutcome('exhausted', durationBucket(30_000));
        continue;
      }

      await this.connect(clientCode, generation, signal, reconnecting);
      return;
    }
  }

  private isCurrent(generation: number): boolean {
    return this.demanded && generation === this.generation;
  }

  private hasActiveAttempt(): boolean {
    return this.abortController !== null || this.connection !== null;
  }

  private releaseConnection(
    connection: RealtimeConnection,
    tileHandler: TileUpdateHandler,
  ): void {
    connection.offTileUpdate(tileHandler);
    if (this.connection === connection) this.connection = null;
    if (this.tileHandler === tileHandler) this.tileHandler = null;
    void connection.stop().catch(() => undefined);
  }

  private transitionTo(state: RealtimeConnectionState): void {
    const previous = this.status.state();
    if (previous === state) return;
    this.status.transitionTo(state);
    this.telemetry.stateTransition(previous, state);
  }

  private recordAttemptOutcome(
    reconnecting: boolean,
    outcome: 'succeeded' | 'failed',
    startedAt: number,
  ): void {
    const duration = durationBucket(performance.now() - startedAt);
    if (reconnecting) this.telemetry.reconnectOutcome(outcome, duration);
    else this.telemetry.connectionOutcome(outcome, duration);
  }
}

function attemptBucket(attempt: number): RealtimeAttemptBucket {
  return Math.min(Math.max(attempt, 1), 10) as RealtimeAttemptBucket;
}

function retryDelayBucket(attempt: number): RealtimeDelayBucket {
  return (['2s', '4s', '8s', '16s', '30s'] as const)[Math.min(attempt - 1, 4)];
}

function durationBucket(milliseconds: number): RealtimeDurationBucket {
  if (milliseconds < 250) return 'under-250ms';
  if (milliseconds < 1_000) return '250ms-to-1s';
  if (milliseconds < 5_000) return '1s-to-5s';
  return '5s-or-more';
}

function isEligibleClientSession(
  session: Session,
): session is Session & { readonly clientCode: string } {
  return session.isAuthenticated
    && session.clientCode !== null
    && session.roles.some((role) => CLIENT_ROLES.includes(role));
}